import React from 'react'
import { Input, Space } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/free-solid-svg-icons'
import InputNumber from '../../InputNumber.jsx'
import Button from '../../Button.jsx'
import type { FilterConfig, FilterLocalState, FilterSelectedKey, RangeFilterValue } from '../TableViewTypes'
import { rangeValueFromSelectedKey } from './filterSelectedKey.ts'
import { FieldTypeEnum } from '../../InputManager/types.ts'

interface RangeFilterDropdownProps {
  columnKey: string
  /**
   * Column's cfg-driven `fieldType` (e.g. 'currency', 'number', 'percent').
   * Drives numeric formatting and (for 'percent') the on-apply
   * percent→decimal translation. Without it we fall back to a plain
   * numeric input with no formatting.
   */
  columnFieldType?: string
  actualFilterKey: string
  filterLocalState: FilterLocalState
  setFilterLocalState: React.Dispatch<React.SetStateAction<FilterLocalState>>
  selectedKeys: FilterSelectedKey[]
  setSelectedKeys: (selectedKeys: FilterSelectedKey[]) => void
  confirm: () => void
  onFiltersChange?: (filters: FilterConfig) => void
}

// Field types that render the formatted numeric input (thousands separator,
// optional currency / percent ornaments). Anything else falls back to a
// plain numeric `Input` without formatting.
const FORMATTED_NUMERIC_FIELD_TYPES = new Set<string>([
  FieldTypeEnum.CURRENCY,
  FieldTypeEnum.NUMBER,
  FieldTypeEnum.VALUATION,
  FieldTypeEnum.PERCENT,
  FieldTypeEnum.RATIO
])

const numberFormatter = (value: string | number | undefined) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
// antd's InputNumber parser is handed `string | undefined`.
const numberParser = (value: string | undefined) => (value ?? '').replace(/,/g, '')

// Percent-specific formatter / parser. The user types the percentage (e.g.
// "12.5"); we display "12.5%" with thousands separator on the integer part.
// On apply the value is divided by 100 so the filter payload matches the
// underlying decimal stored on each row (0.125).
const percentFormatter = (value: string | number | undefined) => {
  if (value === undefined || value === null || value === '') return ''
  return `${numberFormatter(value)}%`
}
const percentParser = (value: string | undefined) => (value ?? '').replace(/%/g, '').replace(/,/g, '')

export default function RangeFilterDropdown({
  columnKey,
  columnFieldType,
  actualFilterKey,
  filterLocalState,
  setFilterLocalState,
  selectedKeys,
  setSelectedKeys,
  confirm,
  onFiltersChange
}: RangeFilterDropdownProps) {
  // Decide formatting from cfg-driven fieldType, NOT from a hardcoded list of
  // column names. Add new numeric column types to FORMATTED_NUMERIC_FIELD_TYPES,
  // not here.
  const isFormattedNumericColumn =
    typeof columnFieldType === 'string' && FORMATTED_NUMERIC_FIELD_TYPES.has(columnFieldType)
  const isPercent = columnFieldType === FieldTypeEnum.PERCENT

  // For percent columns the *stored* range value is a decimal (e.g. 0.10
  // means "10%"), matching the underlying field on data rows. The input
  // shows the user-facing percentage, so we multiply by 100 on display
  // and divide by 100 when storing back.
  // A stored bound is normally a number; the plain-input branch keeps a non-numeric string as typed.
  const toDisplayValue = (v: number | string | null | undefined): number | string | null => {
    if (v === undefined || v === null || v === '') return null
    return isPercent ? Number((Number(v) * 100).toFixed(10)) : v
  }
  const toStoredValue = (v: number | null | undefined): number | null => {
    if (v === undefined || v === null || v === ('' as any)) return null
    return isPercent ? Number((v / 100).toFixed(10)) : v
  }

  const localValue = filterLocalState[columnKey]?.range
  const rangeValue: RangeFilterValue = localValue !== undefined ? localValue : rangeValueFromSelectedKey(selectedKeys[0])
  const hasValue =
    (rangeValue.min !== undefined && rangeValue.min !== null && rangeValue.min !== '') ||
    (rangeValue.max !== undefined && rangeValue.max !== null && rangeValue.max !== '')

  if (localValue !== undefined && JSON.stringify(selectedKeys[0] || {}) !== JSON.stringify(localValue)) {
    setSelectedKeys([localValue])
  }

  const handleApply = () => {
    if (hasValue) {
      setFilterLocalState((prev) => {
        const newState = { ...prev }
        delete newState[columnKey]
        return newState
      })
      confirm()
    }
  }

  const handleClear = () => {
    setFilterLocalState((prev) => {
      const newState = { ...prev }
      delete newState[columnKey]
      return newState
    })

    setSelectedKeys([])

    const filterConfig: FilterConfig = {}
    filterConfig[actualFilterKey] = undefined
    onFiltersChange?.(filterConfig)

    confirm()
  }

  if (isFormattedNumericColumn) {
    return (
      <div style={{ padding: 5, width: 400 }}>
        <div style={{ marginBottom: 8 }}>
          <InputNumber
            placeholder='Min'
            value={toDisplayValue(rangeValue.min) ?? null}
            autoFocus
            onChange={(value: number | null) => {
              const stored = toStoredValue(value)
              const newRange = { ...rangeValue }
              if (stored === null) {
                delete newRange.min
              } else {
                newRange.min = stored
              }
              setFilterLocalState((prev) => ({
                ...prev,
                [columnKey]: { ...prev[columnKey], range: newRange }
              }))
              setSelectedKeys([newRange])
            }}
            formatter={isPercent ? percentFormatter : numberFormatter}
            parser={isPercent ? percentParser : numberParser}
            onPressEnter={handleApply}
            style={{ width: '50%' }}
            step={0.01}
            min={0}
            ref={(input: HTMLInputElement | null) => {
              if (input) {
                const activeElement = document.activeElement
                const isInputFocused =
                  activeElement &&
                  (activeElement.tagName === 'INPUT' ||
                    activeElement.closest('.ant-input-number-input-wrap') ||
                    activeElement.closest('.ant-input-number'))

                if (!isInputFocused) {
                  setTimeout(() => {
                    try {
                      const currentActive = document.activeElement
                      const stillNeedsFocus =
                        !currentActive ||
                        (currentActive.tagName !== 'INPUT' &&
                          !currentActive.closest('.ant-input-number-input-wrap') &&
                          !currentActive.closest('.ant-input-number'))

                      if (stillNeedsFocus) {
                        if ((input as any).input && typeof (input as any).input.focus === 'function') {
                          ;(input as any).input.focus()
                        } else if ((input as any).focus && typeof (input as any).focus === 'function') {
                          ;(input as any).focus()
                        } else if ((input as any).querySelector) {
                          const actualInput = (input as any).querySelector('input')
                          if (actualInput) actualInput.focus()
                        }
                      }
                    } catch (e) {}
                  }, 150)
                }
              }
            }}
          />
          <InputNumber
            placeholder='Max'
            value={toDisplayValue(rangeValue.max) ?? null}
            onChange={(value: number | null) => {
              const stored = toStoredValue(value)
              const newRange = { ...rangeValue }
              if (stored === null) {
                delete newRange.max
              } else {
                newRange.max = stored
              }
              setFilterLocalState((prev) => ({
                ...prev,
                [columnKey]: { ...prev[columnKey], range: newRange }
              }))
              setSelectedKeys([newRange])
            }}
            formatter={isPercent ? percentFormatter : numberFormatter}
            parser={isPercent ? percentParser : numberParser}
            onPressEnter={handleApply}
            style={{ width: '48%', marginLeft: '2%' }}
            step={0.01}
            min={0}
          />
        </div>
        <Space>
          <Button variant='primary' size='small' onClick={handleApply} disabled={!hasValue}>
            Filter
          </Button>
          <Button variant='cancel' size='small' onClick={handleClear}>
            Clear
          </Button>
        </Space>
      </div>
    )
  } else {
    return (
      <div style={{ padding: 8, width: 400 }}>
        <div style={{ marginBottom: 8 }}>
          <Input
            placeholder='Min'
            type='number'
            value={rangeValue.min || ''}
            autoFocus
            onChange={(e) => {
              const value = e.target.value
              const newRange = { ...rangeValue }
              if (value === '' || value === null || value === undefined) {
                delete newRange.min
              } else {
                const numValue = parseFloat(value)
                newRange.min = isNaN(numValue) ? value : numValue
              }
              setFilterLocalState((prev) => ({
                ...prev,
                [columnKey]: { ...prev[columnKey], range: newRange }
              }))
              setSelectedKeys([newRange])
            }}
            onPressEnter={handleApply}
            style={{ width: '45%', marginRight: '10%' }}
            ref={(input) => {
              if (input) {
                const activeElement = document.activeElement
                const isInputFocused = activeElement && activeElement.tagName === 'INPUT'

                if (!isInputFocused) {
                  setTimeout(() => {
                    try {
                      const currentActive = document.activeElement
                      const stillNeedsFocus = !currentActive || currentActive.tagName !== 'INPUT'

                      if (stillNeedsFocus) {
                        if ((input as any).input && typeof (input as any).input.focus === 'function') {
                          ;(input as any).input.focus()
                        } else if (input.focus && typeof input.focus === 'function') {
                          input.focus()
                        } else if ((input as any).querySelector) {
                          const actualInput = (input as any).querySelector('input')
                          if (actualInput) actualInput.focus()
                        }
                      }
                    } catch (e) {}
                  }, 150)
                }
              }
            }}
          />
          <Input
            placeholder='Max'
            type='number'
            value={rangeValue.max || ''}
            onChange={(e) => {
              const value = e.target.value
              const newRange = { ...rangeValue }
              if (value === '' || value === null || value === undefined) {
                delete newRange.max
              } else {
                const numValue = parseFloat(value)
                newRange.max = isNaN(numValue) ? value : numValue
              }
              setFilterLocalState((prev) => ({
                ...prev,
                [columnKey]: { ...prev[columnKey], range: newRange }
              }))
              setSelectedKeys([newRange])
            }}
            onPressEnter={handleApply}
            style={{ width: '45%' }}
          />
        </div>
        <Space>
          <Button variant='primary' size='small' onClick={handleApply} disabled={!hasValue}>
            Filter
          </Button>
          <Button variant='cancel' size='small' onClick={handleClear}>
            Clear
          </Button>
        </Space>
      </div>
    )
  }
}

export function RangeFilterIcon(filtered: boolean) {
  return <FontAwesomeIcon icon={faFilter} style={{ color: filtered ? '#1890ff' : '#9CA3AF', padding: '0 4px' }} />
}
